import { Task } from '../types';
import { generateId } from './Utils';
import { getTaskStartDateValue } from './taskSchedule';

const startOfLocalDay = (date: Date) => {
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);
    return result;
};

const isAfterRecurrenceEndDate = (date: Date, endDate?: string) => {
    if (!endDate) return false;
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    return date > end;
};

const getWeekStart = (date: Date) => {
    const start = startOfLocalDay(date);
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
    return start;
};

const isCalendarIntervalMatch = (candidate: Date, anchor: Date, unit: 'week' | 'month', interval: number) => {
    if (unit === 'week') {
        const elapsedWeeks = Math.round((getWeekStart(candidate).getTime() - getWeekStart(anchor).getTime()) / 604_800_000);
        return elapsedWeeks >= 0 && elapsedWeeks % interval === 0;
    }
    const elapsedMonths = (candidate.getFullYear() - anchor.getFullYear()) * 12 + candidate.getMonth() - anchor.getMonth();
    return elapsedMonths >= 0 && elapsedMonths % interval === 0;
};

const getNextRecurringSchedule = (task: Task, nextDueDate: string): Task['schedule'] => {
    const previousStartValue = getTaskStartDateValue(task);
    if (!task.schedule?.endDate || !previousStartValue) return task.schedule;
    const previousStart = new Date(previousStartValue);
    const previousEnd = new Date(task.schedule.endDate);
    const nextStart = new Date(nextDueDate);
    const nextEnd = new Date(nextStart);

    if (task.schedule.precision === 'month') {
        const months = (previousEnd.getFullYear() - previousStart.getFullYear()) * 12 + previousEnd.getMonth() - previousStart.getMonth();
        nextEnd.setMonth(nextEnd.getMonth() + months);
    } else if (task.schedule.precision === 'year') {
        nextEnd.setFullYear(nextEnd.getFullYear() + previousEnd.getFullYear() - previousStart.getFullYear());
    } else {
        const days = Math.round((previousEnd.getTime() - previousStart.getTime()) / 86_400_000);
        nextEnd.setDate(nextEnd.getDate() + Math.max(0, days));
    }

    return { ...task.schedule, startDate: nextDueDate, endDate: nextEnd.toISOString() };
};

const copySubtasks = (subtasks: Task[] | undefined): Task[] | undefined => {
    if (!subtasks?.length) return undefined;

    return subtasks.map((subtask) => ({
        ...subtask,
        id: generateId(),
        completed: false,
        completedAt: undefined,
        edit: false,
        subtasks: copySubtasks(subtask.subtasks),
    }));
};

export const getNextRecurringDueDate = (task: Task, completedAt = new Date()) => {
    if (!task.recurrence || !task.dueDate) return undefined;

    if (task.recurrence.pattern === 'calendar') {
        const selectedDays = task.recurrence.calendarDays || [];
        const next = new Date(task.dueDate);
        const isWeekly = task.recurrence.unit === 'week';
        const interval = Math.max(1, task.recurrence.interval || 1);
        const anchor = new Date(task.recurrence.calendarAnchorDate || task.dueDate);
        const maxDaysToCheck = isWeekly ? interval * 14 : interval * 62 + 366;

        for (let offset = 1; offset <= maxDaysToCheck; offset += 1) {
            next.setDate(next.getDate() + 1);
            const calendarDay = isWeekly ? next.getDay() : next.getDate();
            if (selectedDays.includes(calendarDay)
                && isCalendarIntervalMatch(next, anchor, task.recurrence.unit as 'week' | 'month', interval)) {
                return isAfterRecurrenceEndDate(next, task.recurrence.endDate) ? undefined : next.toISOString();
            }
        }
        return undefined;
    }

    const next = task.recurrence.mode === 'completion'
        ? new Date(completedAt)
        : new Date(task.dueDate);
    const { interval, unit } = task.recurrence;
    if (unit === 'day') next.setDate(next.getDate() + interval);
    if (unit === 'week') next.setDate(next.getDate() + interval * 7);
    if (unit === 'month') next.setMonth(next.getMonth() + interval);
    if (unit === 'year') next.setFullYear(next.getFullYear() + interval);
    return isAfterRecurrenceEndDate(next, task.recurrence.endDate) ? undefined : next.toISOString();
};

export const createNextRecurringTask = (task: Task, completedAt = new Date()): Task | undefined => {
    const dueDate = getNextRecurringDueDate(task, completedAt);
    if (!dueDate || !task.recurrence) return undefined;
    const remaining = task.recurrence.occurrencesRemaining;
    if (remaining !== undefined && remaining <= 1) return undefined;

    return {
        ...task,
        id: generateId(),
        completed: false,
        edit: false,
        subtasks: task.recurrence.copySubtasks ? copySubtasks(task.subtasks) : undefined,
        dueDate,
        schedule: getNextRecurringSchedule(task, dueDate),
        reminderTime: undefined,
        recurrence: {
            ...task.recurrence,
            calendarAnchorDate: task.recurrence.pattern === 'calendar'
                ? task.recurrence.calendarAnchorDate || task.dueDate
                : undefined,
            occurrencesRemaining: remaining === undefined ? undefined : remaining - 1,
        },
        recurrencePending: true,
    };
};

export const isRecurringTaskPendingForFuture = (task: Task, now = new Date()) => {
    if (!task.recurrencePending || !task.dueDate) return false;
    const dueDate = startOfLocalDay(new Date(task.dueDate));
    return dueDate > startOfLocalDay(now);
};

export const isRecurringTaskDue = (task: Task, now = new Date()) => {
    if (!task.dueDate) return false;
    return startOfLocalDay(new Date(task.dueDate)) <= startOfLocalDay(now);
};

export const getLastOverdueRecurringDueDate = (task: Task, now = new Date()) => {
    if (!task.recurrence || task.recurrence.mode !== 'fixed' || !task.dueDate) return undefined;
    const recurrence = task.recurrence.pattern === 'calendar'
        ? { ...task.recurrence, calendarAnchorDate: task.recurrence.calendarAnchorDate || task.dueDate }
        : task.recurrence;
    let currentTask: Task = { ...task, recurrence };
    let latest = new Date(currentTask.dueDate!);
    const today = startOfLocalDay(now);
    while (true) {
        const candidate = getNextRecurringDueDate(currentTask, now);
        if (!candidate || startOfLocalDay(new Date(candidate)) >= today) return latest.toISOString();
        latest = new Date(candidate);
        currentTask = { ...currentTask, dueDate: latest.toISOString() };
    }
};
